import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { HandHeart, PackageCheck, MapPin, Clock, Store, Plus, Trash2, Flame, ClipboardCheck, Check, HeartHandshake, DollarSign } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusTimeline } from "@/components/StatusTimeline";

const conditionStyle = {
  near_expiry: "bg-orange-50 text-orange-800 border-orange-200",
  damaged: "bg-red-50 text-red-700 border-red-200",
  surplus: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function OrgDashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [needOpen, setNeedOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, l, n, rq, pl] = await Promise.all([api.get("/stats"), api.get("/listings"), api.get("/needs/mine"), api.get("/org/requests"), api.get("/pledges/mine")]);
    setStats(s.data);
    setListings(l.data);
    setNeeds(n.data);
    setRequests(rq.data);
    setPledges(pl.data);
  }, []);

  const markReceived = async (id) => { await api.post(`/pledges/${id}/received`); toast.success("Marked as received"); load(); };

  const approveReq = async (id) => { await api.post(`/requests/${id}/approve`); toast.success("Approved — donors can now fund it"); load(); };
  const declineReq = async (id) => { await api.post(`/requests/${id}/decline`); toast.success("Request declined"); load(); };

  const [schedFor, setSchedFor] = useState(null);
  const [pickupAt, setPickupAt] = useState("");
  const schedule = async () => {
    if (!pickupAt) { toast.error("Pick a date & time"); return; }
    await api.post(`/listings/${schedFor.id}/schedule`, { pickup_at: new Date(pickupAt).toLocaleString() });
    toast.success("Pickup scheduled — the store has been notified.");
    setSchedFor(null); setPickupAt(""); load();
  };

  const addNeed = async (payload) => {
    try {
      await api.post("/needs", payload);
      toast.success("Added to your needs list");
      setNeedOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const removeNeed = async (id) => {
    await api.delete(`/needs/${id}`);
    toast.success("Removed");
    load();
  };

  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    try {
      await api.post(`/listings/${id}/claim`);
      toast.success("Food claimed! Coordinate pickup at the listed address.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const complete = async (id) => {
    await api.post(`/listings/${id}/complete`);
    toast.success("Marked as redistributed. Thank you!");
    load();
  };

  const available = listings.filter((l) => l.status === "available");
  const mine = listings.filter((l) => l.claimed_by === user?.id);

  return (
    <DashboardShell title={`Welcome, ${user?.org_name}`} subtitle={`Food available near ${user?.city || "you"}.`}>
      {user && !user.verified && (
        <div data-testid="verification-banner" className={`rounded-2xl border p-5 flex items-start gap-3 ${user.verification_status === "rejected" ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`}>
          <Clock className={`w-5 h-5 mt-0.5 shrink-0 ${user.verification_status === "rejected" ? "text-red-600" : "text-orange-600"}`} />
          <div>
            <p className={`font-medium ${user.verification_status === "rejected" ? "text-red-800" : "text-orange-900"}`}>
              {user.verification_status === "rejected" ? "Verification not approved" : user.verification_status === "info_requested" ? "More information needed" : "Pending verification"}
            </p>
            <p className={`text-sm mt-0.5 ${user.verification_status === "rejected" ? "text-red-700" : "text-orange-800"}`}>
              {user.verification_note
                ? user.verification_note
                : "Your organization is awaiting verification before appearing publicly. You can use your dashboard now — but you won't show on the public map or receive help requests until an admin approves you."}
            </p>
          </div>
        </div>
      )}
      <div data-testid="org-impact" className="grid sm:grid-cols-2 gap-6">
        <div className="bg-emerald-900 text-emerald-50 rounded-3xl p-8">
          <p className="text-xs tracking-widest uppercase text-emerald-300/80">People helped since joining</p>
          <p className="font-heading text-5xl font-semibold mt-2">{(stats?.people_helped ?? 0).toLocaleString()}</p>
          <p className="text-emerald-200/80 mt-2 text-sm">Estimated from the food you've redistributed. Every pickup counts.</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
          <p className="text-xs tracking-widest uppercase text-stone-500">Meals provided</p>
          <p className="font-heading text-5xl font-semibold text-orange-700 mt-2">{(stats?.meals_provided ?? 0).toLocaleString()}</p>
          <p className="text-stone-500 mt-2 text-sm">{(stats?.redistributed_lbs ?? 0).toLocaleString()} lbs redistributed to your community.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Stat icon={PackageCheck} label="Available nearby" value={stats?.available_nearby} accent="emerald" />
        <Stat icon={HandHeart} label="Claimed by you" value={stats?.claimed} accent="orange" />
        <Stat icon={DollarSign} label="Direct donations received" value={`$${Number(stats?.direct_donations ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
      </div>

      <Tabs defaultValue="available" className="w-full">
        <TabsList className="bg-stone-100 rounded-full p-1">
          <TabsTrigger data-testid="tab-available" value="available" className="rounded-full data-[state=active]:bg-white">Available nearby</TabsTrigger>
          <TabsTrigger data-testid="tab-claimed" value="claimed" className="rounded-full data-[state=active]:bg-white">My pickups</TabsTrigger>
          <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-full data-[state=active]:bg-white">Requests{requests.filter((r) => r.status === "pending").length > 0 ? ` (${requests.filter((r) => r.status === "pending").length})` : ""}</TabsTrigger>
          <TabsTrigger data-testid="tab-donations" value="donations" className="rounded-full data-[state=active]:bg-white">Donations{pledges.filter((p) => p.status === "pledged").length > 0 ? ` (${pledges.filter((p) => p.status === "pledged").length})` : ""}</TabsTrigger>
          <TabsTrigger data-testid="tab-needs" value="needs" className="rounded-full data-[state=active]:bg-white">Needs & profile</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="available-listings">
            {available.length === 0 && <EmptyState text="No food available nearby right now. We'll alert you the moment a store posts." />}
            {available.map((l) => (
              <ListingCard key={l.id} l={l}>
                <Button data-testid={`claim-${l.id}`} onClick={() => claim(l.id)} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">
                  <HandHeart className="w-4 h-4 mr-2" /> Claim this food
                </Button>
              </ListingCard>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="claimed" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="claimed-listings">
            {mine.length === 0 && <EmptyState text="You haven't claimed any food yet." />}
            {mine.map((l) => (
              <ListingCard key={l.id} l={l}>
                <div className="w-full space-y-3">
                  <StatusTimeline status={l.status} />
                  {l.pickup_scheduled_at && <p className="text-sm text-emerald-800 font-medium flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Pickup: {l.pickup_scheduled_at}</p>}
                  {l.status !== "completed" ? (
                    <div className="flex gap-2">
                      <Button data-testid={`schedule-${l.id}`} onClick={() => { setSchedFor(l); setPickupAt(""); }} variant="outline" className="rounded-full flex-1">{l.status === "scheduled" ? "Reschedule" : "Schedule"}</Button>
                      <Button data-testid={`org-complete-${l.id}`} onClick={() => complete(l.id)} className="rounded-full flex-1 bg-emerald-800 hover:bg-emerald-900 text-white">Mark done</Button>
                    </div>
                  ) : (
                    <Badge className="bg-stone-100 text-stone-600 rounded-full">Completed</Badge>
                  )}
                </div>
              </ListingCard>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
            <div className="p-6 border-b border-stone-100">
              <h2 className="font-heading text-xl font-medium text-stone-900">Individual requests</h2>
              <p className="text-sm text-stone-500">People chose your organization as their pickup point. Approve to make it fundable by donors.</p>
            </div>
            <div className="divide-y divide-stone-100" data-testid="org-requests-list">
              {requests.length === 0 && <p className="p-6 text-stone-500">No requests yet.</p>}
              {requests.map((r) => (
                <div key={r.id} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900">{r.item_name}</p>
                      <Badge variant="outline" className={`rounded-full capitalize ${r.status === "open" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>{r.status === "open" ? "approved" : r.status}</Badge>
                    </div>
                    <p className="text-sm text-stone-500 mt-1">For {r.requester_name} · ${Number(r.price).toFixed(2)}{r.note ? ` · "${r.note}"` : ""}</p>
                  </div>
                  {r.status === "pending" && (
                    <div className="flex items-center gap-2">
                      <Button data-testid={`approve-${r.id}`} onClick={() => approveReq(r.id)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white"><Check className="w-4 h-4 mr-1.5" />Approve</Button>
                      <Button data-testid={`decline-${r.id}`} onClick={() => declineReq(r.id)} variant="outline" className="rounded-full">Decline</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="donations" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
            <div className="p-6 border-b border-stone-100 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-heading text-xl font-medium text-stone-900">Direct donations & pledges</h2>
                <p className="text-sm text-stone-500 max-w-xl">Monetary gifts and item pledges donors sent directly to your organization.</p>
              </div>
              <div className="text-right">
                <p className="text-xs tracking-widest uppercase text-stone-500">Money received</p>
                <p className="font-heading text-2xl font-semibold text-emerald-800" data-testid="direct-donations-total">${Number(stats?.direct_donations ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="divide-y divide-stone-100" data-testid="pledges-list">
              {pledges.length === 0 && <p className="p-6 text-stone-500">No item pledges yet. When donors pledge supplies to you, they'll appear here.</p>}
              {pledges.map((p) => (
                <div key={p.id} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><HeartHandshake className="w-5 h-5 text-emerald-800" /></div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-stone-900">{p.items}</p>
                        <Badge variant="outline" className={`rounded-full capitalize ${p.status === "received" ? "bg-stone-100 text-stone-600" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>{p.status}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mt-1">From {p.donor_name}{p.donor_email ? ` · ${p.donor_email}` : ""}{p.note ? ` · "${p.note}"` : ""}</p>
                    </div>
                  </div>
                  {p.status === "pledged" && (
                    <Button data-testid={`pledge-received-${p.id}`} onClick={() => markReceived(p.id)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white"><Check className="w-4 h-4 mr-1.5" />Mark received</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="needs" className="mt-6 space-y-6">
          <ProfileCard user={user} onSaved={refreshUser} />
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
            <div className="flex items-center justify-between p-6 border-b border-stone-100">
              <div>
                <h2 className="font-heading text-xl font-medium text-stone-900">Your needs list</h2>
                <p className="text-sm text-stone-500 max-w-xl">Items beyond groceries, ordered by priority. Donors can buy these on the public "Help Someone Today" page.</p>
              </div>
              <Dialog open={needOpen} onOpenChange={setNeedOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-need-button" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white"><Plus className="w-4 h-4 mr-2" />Add need</Button>
                </DialogTrigger>
                <NeedDialog onSubmit={addNeed} />
              </Dialog>
            </div>
            <div className="divide-y divide-stone-100" data-testid="needs-list">
              {needs.length === 0 && <p className="p-6 text-stone-500">No needs posted yet.</p>}
              {needs.map((n, i) => (
                <div key={n.id} className="p-6 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="font-heading text-lg font-semibold text-stone-300 w-8 text-center">{i + 1}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-stone-900">{n.item_name}</p>
                        <Badge variant="outline" className="rounded-full capitalize">{n.category}</Badge>
                      </div>
                      <p className="text-sm text-stone-500 mt-0.5">${Number(n.estimated_price).toFixed(2)} · {n.quantity_fulfilled}/{n.quantity_needed} fulfilled{n.description ? ` · ${n.description}` : ""}</p>
                    </div>
                  </div>
                  <Button data-testid={`delete-need-${n.id}`} onClick={() => removeNeed(n.id)} variant="ghost" size="icon" className="rounded-full text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Schedule pickup</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Choose when you'll collect <span className="font-medium">{schedFor?.food_type}</span> from {schedFor?.store_name}. The store will be notified.</p>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Pickup date & time</Label>
              <Input data-testid="schedule-datetime" type="datetime-local" value={pickupAt} onChange={(e) => setPickupAt(e.target.value)} className="rounded-xl h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="schedule-confirm" onClick={schedule} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11">Confirm pickup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function ListingCard({ l, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-all flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-medium text-stone-900">{l.food_type}</h3>
          <p className="flex items-center gap-1 text-sm text-stone-500 mt-1"><Store className="w-3.5 h-3.5" /> {l.store_name}</p>
        </div>
        <Badge variant="outline" className={`rounded-full capitalize ${conditionStyle[l.condition] || ""}`}>{l.condition.replace("_", " ")}</Badge>
      </div>
      <p className="font-heading text-2xl font-semibold text-emerald-800 mt-4">{l.quantity}<span className="text-base text-stone-500 ml-1">{l.unit}</span></p>
      <div className="mt-3 space-y-1.5 text-sm text-stone-600">
        <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-stone-400" /> {l.pickup_address || l.city}, {l.city}</p>
        <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-stone-400" /> Available until {l.available_until}</p>
      </div>
      {l.description && <p className="text-sm text-stone-500 mt-3 italic">"{l.description}"</p>}
      <div className="mt-5 pt-5 border-t border-stone-100">{children}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-800 bg-emerald-50" : accent === "orange" ? "text-orange-700 bg-orange-50" : "text-stone-700 bg-stone-100";
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <p className="font-heading text-3xl font-semibold text-stone-900 mt-4">{value ?? 0}</p>
      <p className="text-xs tracking-widest uppercase text-stone-500 mt-1">{label}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="col-span-full bg-white rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-500">{text}</div>;
}

function NeedDialog({ onSubmit }) {
  const [f, setF] = useState({ item_name: "", category: "clothing", quantity_needed: 1, estimated_price: "", priority: 100, description: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...f, quantity_needed: parseInt(f.quantity_needed) || 1, estimated_price: parseFloat(f.estimated_price), priority: parseInt(f.priority) || 100 });
  };
  return (
    <DialogContent className="rounded-2xl">
      <DialogHeader><DialogTitle className="font-heading">Add a needed item</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Item</Label>
          <Input data-testid="need-name" required value={f.item_name} onChange={set("item_name")} placeholder="Warm socks, diapers…" className="rounded-xl h-11" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Category</Label>
            <Select value={f.category} onValueChange={(v) => setF((p) => ({ ...p, category: v }))}>
              <SelectTrigger data-testid="need-category" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{["clothing", "hygiene", "bedding", "medical", "baby", "general"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Est. price (USD)</Label>
            <Input data-testid="need-price" required type="number" step="0.01" min="0" value={f.estimated_price} onChange={set("estimated_price")} className="rounded-xl h-11" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Quantity needed</Label>
            <Input data-testid="need-qty" type="number" min="1" value={f.quantity_needed} onChange={set("quantity_needed")} className="rounded-xl h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Priority (1 = most needed)</Label>
            <Input data-testid="need-priority" type="number" min="1" value={f.priority} onChange={set("priority")} className="rounded-xl h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Notes</Label>
          <Textarea data-testid="need-desc" value={f.description} onChange={set("description")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button data-testid="need-submit" type="submit" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white w-full">Add to needs list</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ProfileCard({ user, onSaved }) {
  const [address, setAddress] = useState(user?.address || "");
  const [warming, setWarming] = useState(!!user?.is_warming_shelter);
  const [services, setServices] = useState((user?.services || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/org/profile", {
        address,
        is_warming_shelter: warming,
        services: services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Profile updated — your map pin is set.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6" data-testid="org-profile-card">
      <h2 className="font-heading text-xl font-medium text-stone-900">Location & shelter profile</h2>
      <p className="text-sm text-stone-500 mb-4">This places you on the public "Find aid near me" map for people in need.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Street address</Label>
          <Input data-testid="profile-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" className="rounded-xl h-11" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Services offered (comma separated)</Label>
          <Input data-testid="profile-services" value={services} onChange={(e) => setServices(e.target.value)} placeholder="Meals, Showers, Beds, Clothing" className="rounded-xl h-11" />
        </div>
        <div className="flex items-center gap-3">
          <Switch data-testid="profile-warming" checked={warming} onCheckedChange={setWarming} />
          <span className="flex items-center gap-1.5 text-sm text-stone-700"><Flame className="w-4 h-4 text-orange-600" /> We are a warming shelter</span>
        </div>
      </div>
      <Button data-testid="profile-save" onClick={save} disabled={saving} className="mt-5 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">
        {saving ? "Saving…" : "Save profile"}
      </Button>
    </div>
  );
}
